import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import {
    ApplicationConfig,
    ErrorHandler,
    inject,
    isDevMode,
    provideAppInitializer,
    importProvidersFrom,
    APP_INITIALIZER,
} from '@angular/core';
import { ApexSafeErrorHandler } from 'app/core/error-handler/apex-safe-error-handler';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withComponentInputBinding } from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { TmaIconsModule } from 'app/core/tma-icons';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { functionKeyInterceptor } from './core/auth/interceptors/function-key.interceptor';
import { authInterceptor } from './core/auth/auth.interceptor';
import { asOfDateInterceptor } from './core/http/as-of-date.interceptor';
import { environment } from 'environments/environment';

// MSAL Imports
import { 
    IPublicClientApplication, 
    PublicClientApplication, 
    BrowserCacheLocation,
    InteractionType 
} from '@azure/msal-browser';
import { 
    MsalService, 
    MsalGuard, 
    MsalInterceptor, 
    MsalBroadcastService,
    MSAL_INSTANCE, 
    MSAL_GUARD_CONFIG, 
    MSAL_INTERCEPTOR_CONFIG,
    MsalGuardConfiguration,
    MsalInterceptorConfiguration
} from '@azure/msal-angular';
import { AuthService } from './core/auth/auth.service';
import { PosthogService } from './core/analytics/posthog.service';
import { VersionCheckService } from './core/version/version-check.service';
import { MetaDashboardCacheService } from './modules/tma/dashboard-shell/services/meta-dashboard-cache.service';
import { NavStructureService }       from './modules/tma/main-dashboards/nav-structure.service';

// MSAL Instance Factory
export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
         auth: {
            clientId: environment.msal.clientId,
            authority: environment.msal.authority,
            redirectUri: window.location.origin,
            postLogoutRedirectUri: window.location.origin
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: false
        },
        system: {
            allowNativeBroker: false,
            loggerOptions: {
                logLevel: 0, // Verbose for debugging
                loggerCallback: (level, message, containsPii) => {
                    if (containsPii) return;
                    switch (level) {
                        case 0: console.error(message); return;
                        case 1: console.warn(message); return;
                        case 2: console.info(message); return;
                        case 3: console.debug(message); return;
                    }
                }
            }
        }
    });
}

// CRITICAL: Initialize MSAL before app starts
export function initializeMsal(msalInstance: IPublicClientApplication) {
    return () => {
        console.log('🔐 Initializing MSAL...');
        return msalInstance.initialize().then(() => {
            console.log('✅ MSAL initialized successfully');
        });
    };
}

// MSAL Guard Configuration
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Popup,
        authRequest: {
            scopes: ['user.read', environment.msal.apiScope]
        }
    };
}

// MSAL Interceptor Configuration
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    return {
        interactionType: InteractionType.Popup,
        protectedResourceMap: new Map([
            ['https://graph.microsoft.com/v1.0/me', ['user.read']],
            //[environment.apiUrl, ['api://triadmedicalanalytics.com/access_as_user']]
        ])
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: ErrorHandler, useClass: ApexSafeErrorHandler },
        provideAnimations(),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withComponentInputBinding()
        ),
        
        // HTTP Client configuration - BOTH interceptor types
        provideHttpClient(
            withInterceptorsFromDi(), // This enables class-based interceptors (MSAL)
            withInterceptors([authInterceptor, functionKeyInterceptor, asOfDateInterceptor]) // Functional interceptors
        ),

        // MSAL Providers - ALL REQUIRED
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        // CRITICAL: Initialize MSAL before anything else runs
        {
            provide: APP_INITIALIZER,
            useFactory: initializeMsal,
            deps: [MSAL_INSTANCE],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (authService: AuthService) => () => authService.initialize(),
            deps: [AuthService],
            multi: true
        },
        // Pull MetaDashboard configs from Cosmos at boot (global-only).
        // Failure is non-fatal — the renderer falls back to bundled TS imports.
        {
            provide: APP_INITIALIZER,
            useFactory: (cache: MetaDashboardCacheService) => () => cache.bootstrap(),
            deps: [MetaDashboardCacheService],
            multi: true
        },
        // Pull the practice nav structure from Cosmos at boot.
        // NAV_STRUCTURE_V1~practice-detail drives sidebar / top bars / column
        // grids / admin tools. Failure falls back to PRACTICE_NAV_STRUCTURE
        // TS const so the app never breaks on a missing nav doc.
        {
            provide: APP_INITIALIZER,
            useFactory: (svc: NavStructureService) => () => svc.bootstrap(),
            deps: [NavStructureService],
            multi: true
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalBroadcastService,
        MsalGuard,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        // PostHog product analytics — init before auth completes; identify()
        // fires later from AuthState.setAuthenticated().
        provideAppInitializer(() => {
            inject(PosthogService).initialize();
        }),
        // Auto-update: poll version.json for a newer deployed build and
        // reload at a non-disruptive moment. No-op for local dev builds.
        provideAppInitializer(() => {
            inject(VersionCheckService).initialize();
        }),

        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);

            return firstValueFrom(translocoService.load(defaultLang));
        }),

        provideAuth(),
        provideIcons(),
        importProvidersFrom(TmaIconsModule),
        provideFuse({
            fuse: {
                layout: 'classy',
                scheme: 'dark',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};